<template>
    <Bar
        ref="chart"
        :data="computedData"
        :options="chartOptions"
        :plugins="plugins"
    />
</template>

<script>
import moment from 'moment'
import { Bar } from 'vue-chartjs'
import {
    Utils,
    Chart,
    BarController,
    BarElement,
    LinearScale,
    CategoryScale,
    TimeSeriesScale,
    Title,
    Legend,
    Filler,
    Tooltip
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
Chart.register(BarController, BarElement, LinearScale, CategoryScale, TimeSeriesScale, Title, Legend, Filler, Tooltip, ChartDataLabels);

export default {
    name: 'BarChartStacked',
    components: {
        Bar
    },
    props: {
        data: {
            type: Object,
            default: {}
        },
        options: {
            type: Object,
            default: {}
        },
        plugins: {
            type: Array,
            default: []
        },
        chartData: {
            type: Object,
            default: {
                options: {

                }
            }
        },
        tooltipDefaultConfig: {
            type: Object,
            default: {

            }
        },
        hasData: {
            type: Boolean,
            default: false
        },
        feTheme: {
            type: Object,
            default: {
                charts_no_data_text: 'No data'
            }
        }
    },

    data() {
        return {
            firstLoad: true,
            cdOptions: this.chartData.options,
            formats: this.chartData.options.period ? {
                day: this.chartData.options.period.format['day'],
                week: this.chartData.options.period.format['week'],
                month: this.chartData.options.period.format['month'],
                year: this.chartData.options.period.format['year'],
            } : null,
        }
    },

    computed: {
        minDate() {
            return new Date(this.chartData.options.minDate * 1000)
        },
        maxDate() {
            return new Date(this.chartData.options.maxDate * 1000)
        },
        computedData() {
            if ( ! this.hasData ) {
                let data = JSON.parse(JSON.stringify(this.data));
                data.datasets = [
                    {
                        label: this.feTheme.charts_no_data_text,
                        data: [1, 2, 3, 4, 2, 0],
                        borderColor: '#c1d2c1',
                        backgroundColor: '#d1d1d1',
                    }
                ]
                data.labels = ['', '', '', '', '', '']
                return data
            }
            return this.data
        },
        chartOptions() {
            const _this = this,
                cdOptions = this.cdOptions

            const scalesConfig = {
                x : {
                    stacked : true
                }
            };

            // console.log('reference data set', data);


            if ( cdOptions.x_axis === 'time' && cdOptions.y_axis === 'percentage') {

                let xType = this.hasData ? 'time' : 'category';

                let xMin = this.hasData ? this.minDate : 0,
                    xMax = this.hasData ? this.maxDate : 6;

                /*
                 * If it's the first time when the page loads we set the last point on X-axis to be the current date,
                 * and also set the this.firstLoad to false so that if the user starts using the filter it will use the this.maxDate
                 *
                 * We also modify the start dates so that if:
                 * Chart period = daily => start date is set to -1 month
                 * Chart period = weekly => start date is set to -6 months
                 * Chart period = monthly => start date is set to -1 year
                 * Chart period = yearly => start date is set to -6 years
                 *
                 * Was discussed here: https://gitlab.com/ryter/custom/boxed/plugin/-/issues/275#note_1664201113
                 */
                if(this.firstLoad) {
                    this.firstLoad = false;

                    const period = this.chartData.period,
                        date = moment();

                    let startDate;

                    if(period && period === 'daily') {
                        startDate = date.subtract(1, 'months')
                    } else if(period && period === 'weekly') {
                        startDate = date.subtract(6, 'months')
                    } else if(period && period === 'monthly') {
                        startDate = date.subtract(1, 'years')
                    } else if(period && period === 'yearly') {
                        startDate = date.subtract(6, 'years')
                    } else {
                        startDate = date;
                    }

                    xMin = this.hasData ? startDate.valueOf() : 0
                    xMax = this.hasData ? Date.now() : 6
                }

                scalesConfig.x = {
                    type: xType,
                    min: xMin,
                    max: xMax,
                    stacked : true,
                    time : {
                        unit: cdOptions.period.unit,
                        round: cdOptions.period.unit,
                        // displayFormats: _.bind( _this.chartDisplayFormat, {options} )
                    },
                    ticks: {
                        callback: function( value ) {
                            if ( ! _this.hasData ) return ''
                            let tickLabel = this.format(value, _this.formats[cdOptions.period.unit] )
                            if ( 'week' == cdOptions.period.unit ) {
                                return `W${tickLabel}`
                            } else {
                                return tickLabel
                            }
                        }
                    }
                }

                scalesConfig.y = {
                    beginAtZero: true,
                    suggestedMax: 100,
                    suggestedMin: cdOptions.min,
                    stacked: true,
                    max: 100,
                    grid: {
                        tickLength: 10,
                        drawOnChartArea: true
                    },
                    ticks: {
                        stepSize: 20,
                        precision: 0,
                        callback: function (value) {
                            return value + '%'; // convert it to percentage
                        }
                    }
                }

            } else if ( cdOptions.x_axis === 'time' && cdOptions.y_axis === 'numbers') {

                let xType = this.hasData ? 'time' : 'category',
                    xMin = this.hasData ? this.minDate : 0,
                    xMax = this.hasData ? this.maxDate : 5

                /*
                 * If it's the first time when the page loads we set the last point on X-axis to be the current date,
                 * and also set the this.firstLoad to false so that if the user starts using the filter it will use the this.maxDate
                 *
                 * We also modify the start dates so that if:
                 * Chart period = daily => start date is set to -1 month
                 * Chart period = weekly => start date is set to -6 months
                 * Chart period = monthly => start date is set to -1 year
                 * Chart period = yearly => start date is set to -6 years
                 *
                 * Was discussed here: https://gitlab.com/ryter/custom/boxed/plugin/-/issues/275#note_1664201113
                 */
                if(this.firstLoad) {
                    this.firstLoad = false;

                    const period = this.chartData.period,
                        date = moment();

                    let startDate;

                    if(period && period === 'daily') {
                        startDate = date.subtract(1, 'months')
                    } else if(period && period === 'weekly') {
                        startDate = date.subtract(6, 'months')
                    } else if(period && period === 'monthly') {
                        startDate = date.subtract(1, 'years')
                    } else if(period && period === 'yearly') {
                        startDate = date.subtract(6, 'years')
                    } else {
                        startDate = date;
                    }

                    xMin = this.hasData ? startDate.valueOf() : 0
                    xMax = this.hasData ? Date.now() : 6
                }

                scalesConfig.x = {
                    type : xType,
                    stacked : true,
                    min: xMin,
                    max: xMax,
                    time : {
                        unit: cdOptions.period.unit,
                        round: cdOptions.period.unit,
                        // displayFormats: _.bind( _this.chartDisplayFormat, {cdOptions} )
                    },
                    ticks: {
                        callback: function( value ) {
                            if ( ! _this.hasData ) return ''
                            let tickLabel = this.format(value, _this.formats[cdOptions.period.unit] )
                            if ( 'week' == cdOptions.period.unit ) {
                                return `W${tickLabel}`
                            } else {
                                return tickLabel
                            }
                        }
                    }
                }
                scalesConfig.y = {
                    beginAtZero: true,
                    suggestedMax: cdOptions.max ? cdOptions.max : false,
                    suggestedMin: cdOptions.min,
                    stacked: true,
                    grid: {
                        tickLength: 10,
                        drawOnChartArea: true
                    },
                    ticks: {
                        stepSize: cdOptions.step_size_y ? cdOptions.step_size_y : .5,
                        precision: 0,
                        callback: function (value) {
                            return value
                        },
                    }
                }
            } else if(cdOptions.x_axis === 'groups' && cdOptions.y_axis === 'percentage') {
                scalesConfig.y = {
                    stacked: true,
                    suggestedMax: 100,
                    ticks: {
                        stepSize: 20,
                        min: 0,
                        max: cdOptions.max ? cdOptions.max : 100,// Your absolute max value
                        callback: function (value) {
                            return value + '%'; // convert it to percentage
                        },
                        // precision: 0,

                    },
                    scaleLabel: {
                        display: true,
                        labelString: 'Percentage',
                    }
                }
            } else if ( cdOptions.x_axis === 'groups' && cdOptions.y_axis === 'numbers' ) {
                scalesConfig.y = {
                    beginAtZero: true,
                    suggestedMax: cdOptions.max ? cdOptions.max : false,
                    suggestedMin: cdOptions.min,
                    stacked: true,
                    grid: {
                        tickLength: 10,
                        drawOnChartArea: true
                    },
                    ticks: {
                        stepSize: cdOptions.step_size_y ? cdOptions.step_size_y : .5,
                        precision: 0,
                        callback: function (value) {
                            return value; // convert it to percentage
                        },
                    }
                };
            }

            return {
                responsive: true,
                interaction: {
                    intersect: false,
                },
                pointBackgroundColor: '#fff',
                plugins: {
                    legend: {
                        position: 'top',
                    },

                    filler: {
                        propagate: true,
                    },

                    tooltip: {
                        ...this.tooltipDefaultConfig,

                        callbacks: {
                            title: function(context){
                                return _this.tooltipTitleCallback( context, this )
                            },
                            labelColor: function(context) {
                                // console.log()
                                return {
                                    borderColor: context.dataset.borderColor,
                                    backgroundColor: context.dataset.backgroundColor,
                                    borderWidth: 0,
                                    borderRadius: 0,
                                    marginRight: 10,
                                };
                            },
                            labelTextColor: function(context) {
                                return '#fff';
                            },

                            label: function(context) {
                                let label = context.dataset.label + ' ' + context.dataset.data[context.dataIndex].y || '';
                                return ( cdOptions.y_axis === 'percentage' ) ? label + '%' : label + ''
                            },
                        }
                    },
                    datalabels: {
                        display: false,
                    },
                },
                maintainAspectRatio: false,
                scales: scalesConfig
            }
        }
    },
    methods: {
        xTicksWeeksCallback(value, index, values) {
            if(this.chartData.options.period.unit === 'week') {
                // return `W${value}`;
            } else {
                return value;
            }
        },
        tooltipTitleCallback(context, a) {
            if ( this.chartData.options.period ) {
                let unit = this.chartData.options.period.unit
                return `${'week' == unit ? 'W' : ''}${moment(new Date(context[0].raw.x))
                    .format(window.date_format)}`
            }
        }
    }

}
</script>